import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import { checkAdminSession, completeAdminSessionTransfer, adminLogout } from "../../common/session";
import { loginWithPopup, isAbandonedAuth } from "../../common/authPopup";
import AdminService from "./AdminService";
const AdminAuthContext = createContext(null);
import { useNavigate } from "react-router";

export const AdminAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [canManageCustomers, setCanManageCustomers] = useState(false);
    const [checkingManageAccess, setCheckingManageAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    /**
     * Populate auth state from a /api/me user object.
     * Only activates for admin sessions (isAdmin === true).
     */
    const setAuthFromUser = useCallback((user) => {
        if (!user) return false;
        const roles = user.roles || [];
        setUserInfo(user);
        setUserRoles(roles);
        setIsAuthenticated(true);
        return true;
    }, []);

    /**
     * Check Manage Customers Access
     */
    const checkManageCustomersAccess = async () => {
        try {
            setCheckingManageAccess(true);
            const res = await AdminService.checkManageCustomers();
            const isAuthorized = res?.isAuthorized === true;
            setCanManageCustomers(isAuthorized);
        } catch (err) {
            console.error("ManageCustomers check failed:", err);
            setCanManageCustomers(false);
        } finally {
            setCheckingManageAccess(false);
        }
    };

    /**
     * On mount — check if we already have a valid admin session cookie.
     */
    const initSession = useCallback(async () => {
        try {
            const { authenticated, user } = await checkAdminSession();
            if (authenticated && user?.isAdmin) {
                setAuthFromUser(user);
                await checkManageCustomersAccess();
            }
        } catch (err) {
            console.error("Admin session check error:", err);
        } finally {
            setLoading(false);
        }
    }, [setAuthFromUser]);

    /**
     * Exchange the popup's transfer token for an admin session.
     * Driven by loginWithPopup, which handles both the postMessage fast path
     * and the COOP-proof server rendezvous.
     */
    const completeAdminLogin = useCallback(async (transferToken) => {
        try {
            const { authenticated, user } = await completeAdminSessionTransfer(transferToken);

            if (authenticated && user) {
                setAuthFromUser(user);

                // Call onboarding API after successful login
                if (user.tenantId) {
                    await AdminService.postAdminOnboarding();
                }
                await checkManageCustomersAccess();
            }
        } catch (err) {
            console.error("Admin auth transfer failed:", err);
        }
    }, [setAuthFromUser]);

    const login = async () => {
        try {
            const { transferToken } = await loginWithPopup({
                loginPath: "/login?target=admin",
                windowName: "unaric_admin_auth",
                successType: "admin-auth-success",
                // failAuthFlow on the server emits acs-auth-error for every
                // failure regardless of target, so the fast path needs to
                // match it. The poll path carries the error either way.
                errorType: "acs-auth-error",
            });
            await completeAdminLogin(transferToken);
        } catch (err) {
            if (isAbandonedAuth(err)) return;
            console.error("Admin login failed:", err);
        }
    };

    const logout = async () => {
        await adminLogout();
        setUserInfo(null);
        setUserRoles([]);
        setIsAuthenticated(false);
        setCanManageCustomers(false);
        navigate("/admin");
        window.location.reload();
    };

    /**
     * Role Check Helpers
     */
    const hasRole = (role) => userRoles.includes(role);

    const hasAnyRole = (roles = []) =>
        roles.some((role) => userRoles.includes(role));

    useEffect(() => {
        initSession();
    }, [initSession]);

    return (
        <AdminAuthContext.Provider
            value={{
                isAuthenticated,
                userInfo,
                userRoles,
                login,
                logout,
                hasRole,
                hasAnyRole,
                canManageCustomers,
                checkingManageAccess,
                loading,
            }}
        >
            {children}
        </AdminAuthContext.Provider>
    );
};

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error("useAdminAuth must be used inside AdminAuthProvider");
    }
    return context;
};
